import { ReactNode } from 'react';

import { RiDeleteBinLine, RiMore2Fill, RiPencilLine } from '@remixicon/react';

import {
  ButtonIcon,
  Menu,
  MenuRoot,
  MenuRow,
} from '@spotify-internal/encore-web';
import { Popover } from '@spotify-internal/encore-web/next';
import { Copy, StringUtils } from '@spotify/confidence-core-react';

import styles from './ArtifactMenu.module.css';

interface ItemProps {
  icon: ReactNode;
  children: string;
  onClick?: () => void;
  destructive?: boolean;
  disabled?: boolean;
}

const Item = ({
  icon,
  children,
  onClick,
  destructive,
  disabled,
}: ItemProps) => (
  <MenuRow
    textValue={children}
    onAction={onClick}
    isDisabled={disabled}
    className={destructive ? styles.destructive : undefined}
  >
    {icon}
    {children}
  </MenuRow>
);

const buildRichText = (text: string, href: string): Record<string, string> => ({
  'text/plain': text,
  'text/html': `<a href="${StringUtils.escapeHtml(href)}">${StringUtils.escapeHtml(text)}</a>`,
});

interface RootProps {
  children?: ReactNode;
  'aria-label': string;
  copyUrl?: string;
  displayName?: string;
  onOpen?: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}

const Root = ({
  children,
  'aria-label': ariaLabel,
  copyUrl,
  displayName,
  onOpen,
  onRename,
  onDelete,
}: RootProps) => (
  <MenuRoot
    onOpenChange={isOpen => {
      if (isOpen) onOpen?.();
    }}
  >
    <ButtonIcon
      variant="tertiary"
      size="small"
      aria-label={ariaLabel}
      onClick={e => e.stopPropagation()}
    >
      <RiMore2Fill size={16} />
    </ButtonIcon>
    <Popover hasNoArrow onClick={e => e.stopPropagation()}>
      <Menu aria-label={ariaLabel}>
        {children}
        {copyUrl && (
          <Copy value={copyUrl} component={Item} label="Copy URL" icon="link" />
        )}
        {displayName && (
          <Copy
            value={copyUrl ? buildRichText(displayName, copyUrl) : displayName}
            component={Item}
            label="Copy name"
            icon="copy"
          />
        )}
        {onRename && (
          <Item icon={<RiPencilLine size={16} />} onClick={onRename}>
            Rename
          </Item>
        )}
        {onDelete && (
          <Item
            icon={<RiDeleteBinLine size={16} />}
            onClick={onDelete}
            destructive
          >
            Delete
          </Item>
        )}
      </Menu>
    </Popover>
  </MenuRoot>
);

export const ArtifactMenu = {
  Root,
  Item,
};
